import { useCallback, useEffect, useRef, useState } from 'react';

import { adminApi } from '../adminApi';
import { CRAWL_POLL_MS } from '../crawlRuns';
import { StatusBadge } from './StatusBadge';

// The "Search index" cell on the device page's manuals table (task
// manual_ingest_admin_ui, ADR 0097): per PUBLISHED manual, shows whether the
// Docling ingestion that makes it keyword-searchable is queued / parsing /
// indexed / failed, with the parsed counts, an inline error drill-down, and a
// Re-ingest control. Polls GET /v1/admin/manuals/:id/ingestion while the parse
// is active (reusing the crawl poll cadence) and stops on a terminal state.
//
// Reuses the admin design system verbatim: StatusBadge colors (with a label
// override), the crawl-error toggle/message treatment for the failed-state
// expand (the error text is already in the GET response — no second fetch, unlike
// CrawlRunErrors), .admin-button-secondary for the non-destructive Re-ingest, and
// .admin-run-strip-updated for the muted counts sub-line.

type IngestionStatus = 'queued' | 'running' | 'succeeded' | 'failed';

interface Ingestion {
  manual_id: string;
  status: IngestionStatus;
  page_count: number | null;
  chunk_count: number | null;
  figure_count: number | null;
  docling_version: string | null;
  error: string | null;
  // Fleet host running the parse (e.g. 'fsn-app-01') — jobs.locked_host, joined
  // in by the ingestion endpoint (ADR 0121). Non-null only while a job holds the
  // lease (parsing); null once it settles or before a queued job is claimed.
  // Admin-only: this cell never renders on an owner surface.
  host: string | null;
  // Retry diagnostics from the current parse_manual job (ADR 0122), joined in by
  // the ingestion endpoint and non-null only while the parse is ACTIVE. `attempts`
  // is the claim count (1 = first run; > 1 = it has retried); `last_error` is the
  // most recent transient failure — WHY it's retrying (OOM / 5xx / transport).
  // These turn a frozen "Parsing…" into an attributable "retrying, because …".
  attempts: number | null;
  max_attempts: number | null;
  last_error: string | null;
  started_at: string | null;
  finished_at: string | null;
  updated_at: string;
}

// 'loading' before the first read; 'none' when the manual was never ingested
// (GET 404 — e.g. published before Docling was configured); 'error' on a read
// failure; otherwise the fetched ingestion row.
type CellState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'have'; ingestion: Ingestion }
  | { kind: 'error'; message: string };

const ACTIVE: ReadonlySet<IngestionStatus> = new Set<IngestionStatus>(['queued', 'running']);

// Map each ingestion state onto an existing badge color + a product-meaningful
// label ("Indexed" tells the operator the manual is now searchable, not just that
// a job finished).
const BADGE: Record<IngestionStatus, { status: string; label: string }> = {
  queued: { status: 'running', label: 'Queued' },
  running: { status: 'running', label: 'Parsing…' },
  succeeded: { status: 'succeeded', label: 'Indexed' },
  failed: { status: 'failed', label: 'Failed' },
};

export function ManualIngestionCell({
  manualId,
  published,
  onSettled,
}: {
  manualId: string;
  published: boolean;
  // Optional. Called when this manual's ingestion transitions INTO a terminal
  // state (succeeded / failed) during the cell's lifetime — e.g. a poll or a
  // re-ingest completing. Lets a parent (the Device-manuals workspace) refresh a
  // fleet summary so a re-indexed manual leaves the needs-attention worklist and
  // the header counts follow. Deliberately NOT fired on the first observed status
  // (no transition), so a table of already-settled rows doesn't trigger N
  // refreshes on mount. Default unset → the device page is unaffected.
  onSettled?: (status: IngestionStatus) => void;
}): React.ReactElement {
  const [state, setState] = useState<CellState>({ kind: 'loading' });
  const [expanded, setExpanded] = useState(false);
  const [reingesting, setReingesting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Latest onSettled in a ref so `load` stays stable (deps [manualId]) and the
  // poll interval below doesn't churn when the parent passes a fresh closure.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  // The last ingestion status this cell observed, to tell a real transition
  // (fire onSettled) apart from the first load (don't).
  const prevStatusRef = useRef<IngestionStatus | 'none' | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await adminApi<{ ingestion: Ingestion }>(
        `/v1/admin/manuals/${manualId}/ingestion`
      );
      setState({ kind: 'have', ingestion: res.ingestion });
      const next = res.ingestion.status;
      const prev = prevStatusRef.current;
      prevStatusRef.current = next;
      // Fire only on a transition INTO a terminal state (not the first observation).
      if (prev !== null && prev !== next && (next === 'succeeded' || next === 'failed')) {
        onSettledRef.current?.(next);
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) {
        setState({ kind: 'none' });
        prevStatusRef.current = 'none';
        return;
      }
      setState({ kind: 'error', message: 'Couldn’t load the index status.' });
    }
  }, [manualId]);

  // Initial read (published rows only).
  useEffect(() => {
    if (!published) return;
    let cancelled = false;
    void (async () => {
      await load();
      void cancelled;
    })();
    return () => {
      cancelled = true;
    };
  }, [published, load]);

  // Poll while this manual's parse is active; stop on a terminal/none/error state.
  // Published manuals per device are few, so one interval per active row is fine.
  const activeStatus = state.kind === 'have' && ACTIVE.has(state.ingestion.status);
  useEffect(() => {
    if (!activeStatus) return;
    const timer = setInterval(() => void load(), CRAWL_POLL_MS);
    return () => clearInterval(timer);
  }, [activeStatus, load]);

  const reingest = useCallback(async (): Promise<void> => {
    setReingesting(true);
    setActionError(null);
    try {
      const res = await adminApi<{ ingestion: Ingestion | null }>(
        `/v1/admin/manuals/${manualId}/reingest`,
        { method: 'POST' }
      );
      if (res.ingestion !== null) setState({ kind: 'have', ingestion: res.ingestion });
      else await load();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setActionError(
        status === 503
          ? 'Manual indexing isn’t configured on this environment.'
          : status === 409
            ? 'Can’t re-ingest this manual.'
            : 'Couldn’t start re-ingestion.'
      );
    } finally {
      setReingesting(false);
    }
  }, [manualId, load]);

  if (!published) return <>—</>;
  if (state.kind === 'loading') return <span className="admin-run-strip-updated">…</span>;
  if (state.kind === 'error') return <span className="admin-inline-error">{state.message}</span>;

  const reingestButton = (
    <button
      type="button"
      className="admin-button-secondary"
      onClick={() => void reingest()}
      disabled={reingesting || activeStatus}
    >
      {reingesting ? 'Re-ingesting…' : 'Re-ingest'}
    </button>
  );

  if (state.kind === 'none') {
    return (
      <div className="admin-ingestion-cell">
        <div className="admin-ingestion-line">
          <StatusBadge status="manual" label="Not indexed" />
          {reingestButton}
        </div>
        {actionError !== null ? <span className="admin-inline-error">{actionError}</span> : null}
      </div>
    );
  }

  const ing = state.ingestion;
  const badge = BADGE[ing.status];
  return (
    <div className="admin-ingestion-cell">
      <div className="admin-ingestion-line">
        <StatusBadge status={badge.status} label={badge.label} />
        {/* Which fleet host picked up the parse (admin-only infra detail,
            ADR 0121). Shown only while the parse is ACTIVE, so it rides alongside
            the "Parsing…"/"Queued" badge and disappears once indexed. The API
            already nulls host on a terminal status, but we also gate on the active
            set here so a stale host from the ingestion-status / lease-cleanup race
            can never pin "Indexed · on fsn-app-XX" after polling stops (Codex
            review). The "· on" connector binds it to the badge as metadata. */}
        {ing.host !== null && ACTIVE.has(ing.status) ? (
          <span className="admin-ingestion-host">· on {ing.host}</span>
        ) : null}
        {/* Retry progress + reason (ADR 0122). A parse that has retried (attempts
            > 1) otherwise looks identical to a healthy one — a frozen "Parsing…" —
            so surface the attempt count, and let the operator expand the last
            transient error to see WHY it's retrying (OOM / 5xx / transport). Active
            only; the API nulls these on a terminal status. Reuses the muted host-chip
            treatment and the failed-state "Why?" toggle for visual consistency. */}
        {ACTIVE.has(ing.status) && ing.attempts !== null && ing.attempts > 1 ? (
          <span className="admin-ingestion-host">
            · attempt {ing.attempts}
            {ing.max_attempts !== null ? `/${ing.max_attempts}` : null}
          </span>
        ) : null}
        {/* "Why retrying?" / "Why?" disclosure (ADR 0122). Written with the shared
            .admin-link-button reset + .crawl-error-toggle tint + the ▸/▾ caret, so
            it matches the crawl-errors table's disclosure exactly instead of showing
            native button chrome. On an ACTIVE retrying row the `is-retrying` modifier
            recolours it from red to the amber "Parsing…" cluster — a hiccup to expand,
            not a failure; a failed row keeps red (a terminal error to open). */}
        {ACTIVE.has(ing.status) && ing.last_error !== null ? (
          <button
            type="button"
            className="admin-link-button crawl-error-toggle is-retrying"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> Why retrying?
          </button>
        ) : null}
        {ing.status === 'failed' ? (
          <button
            type="button"
            className="admin-link-button crawl-error-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
          >
            <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> Why?
          </button>
        ) : null}
        {reingestButton}
      </div>
      {ing.status === 'succeeded' ? (
        <div
          className="admin-run-strip-updated"
          title={ing.docling_version !== null ? `Docling ${ing.docling_version}` : undefined}
        >
          {ing.page_count ?? 0} pp · {ing.chunk_count ?? 0} chunks · {ing.figure_count ?? 0} figures
        </div>
      ) : null}
      {ing.status === 'failed' && expanded && ing.error !== null ? (
        <div className="admin-ingestion-reason">
          <div className="crawl-error-message">{ing.error}</div>
        </div>
      ) : null}
      {ACTIVE.has(ing.status) && expanded && ing.last_error !== null ? (
        <div className="admin-ingestion-reason is-retrying">
          <div className="crawl-error-message">{ing.last_error}</div>
        </div>
      ) : null}
      {actionError !== null ? <span className="admin-inline-error">{actionError}</span> : null}
    </div>
  );
}
